import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Clock, DollarSign, User, Home, Calendar } from 'lucide-react';
import { bookingAPI, paymentAPI, propertyAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_FLOW = ['pending', 'confirmed', 'checked_in', 'checked_out'];

const BookingDetailPage = ({ isNew }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManage, user } = useAuth();
  
  // Existing booking state
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [updating, setUpdating] = useState(false);
  const [checkInCode, setCheckInCode] = useState('');
  const [checkInInstructions, setCheckInInstructions] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  // New booking form state
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState({ adults: 1, children: 0, infants: 0 });
  const [specialRequests, setSpecialRequests] = useState('');
  const [guestMessage, setGuestMessage] = useState('');
  const [availability, setAvailability] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch existing booking
  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    bookingAPI.getOne(id)
      .then(res => {
        setBooking(res.data.data);
        setCheckInCode(res.data.data.checkInCode || '');
        setCheckInInstructions(res.data.data.checkInInstructions || '');
        setInternalNotes(res.data.data.internalNotes || '');
      })
      .catch(() => toast.error('Failed to load booking'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  // Fetch properties for new booking
  useEffect(() => {
    if (isNew) {
      const fetchProperties = async () => {
        try {
          const res = await propertyAPI.getAll({ limit: 100 });
          setProperties(res.data.data || []);
        } catch (err) {
          toast.error('Failed to load properties');
        }
      };
      fetchProperties();
    }
  }, [isNew]);

  // Check availability for new booking
  useEffect(() => {
    if (isNew && selectedProperty && checkIn && checkOut) {
      const checkAvailability = async () => {
        try {
          const res = await bookingAPI.checkAvailability({
            propertyId: selectedProperty,
            checkIn,
            checkOut
          });
          setAvailability(res.data.data);
        } catch (err) {
          setAvailability({ isAvailable: false });
        }
      };
      checkAvailability();
    }
  }, [isNew, selectedProperty, checkIn, checkOut]);

  const updateStatus = async (status) => {
    setUpdating(true);
    try {
      const res = await bookingAPI.updateStatus(id, { status, checkInCode, checkInInstructions, internalNotes });
      setBooking(res.data.data);
      toast.success(`Booking ${status.replace('_', ' ')}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  const cancelBooking = async () => {
    if (!window.confirm('Cancel this booking?')) return;
    setUpdating(true);
    try {
      await bookingAPI.cancel(id, 'Cancelled by admin');
      const res = await bookingAPI.getOne(id);
      setBooking(res.data.data);
      toast.success('Booking cancelled');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cancel failed');
    } finally {
      setUpdating(false);
    }
  };

  const updatePaymentStatus = async (status) => {
    if (!window.confirm(`Change payment status to ${status}?`)) return;
    setUpdating(true);
    try {
      if (status === 'paid') {
        await paymentAPI.confirmBooking(id);
      } else if (status === 'refunded') {
        await paymentAPI.refund(id, { amount: booking.pricing?.total, reason: 'Admin refund' });
      }
      const res = await bookingAPI.getOne(id);
      setBooking(res.data.data);
      toast.success(`Payment ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment update failed');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  // Show creation form for new bookings
  if (isNew) {
    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!selectedProperty || !checkIn || !checkOut) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (!availability?.isAvailable) {
        toast.error('Property is not available for selected dates');
        return;
      }

      setSubmitting(true);
      try {
        const res = await bookingAPI.create({
          propertyId: selectedProperty,
          checkIn,
          checkOut,
          guests,
          specialRequests,
          guestMessage
        });
        toast.success('Booking created successfully');
        navigate(`/bookings/${res.data.data._id}`);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to create booking');
      } finally {
        setSubmitting(false);
      }
    };

    const calculateNights = () => {
      if (!checkIn || !checkOut) return 0;
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    };

    const nights = calculateNights();
    const pricing = availability?.property?.pricing;

    return (
      <div>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-ghost btn-icon" onClick={() => navigate('/bookings')}><ArrowLeft size={20} /></button>
            <div>
              <h1 className="page-title">New Booking</h1>
              <p className="page-subtitle">Create a new property booking</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          {/* Left column - Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Property & Dates</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 20 }}>
                <div className="form-group">
                  <label className="form-label">Property *</label>
                  <select 
                    className="form-select" 
                    value={selectedProperty || ''} 
                    onChange={e => setSelectedProperty(e.target.value)}
                    required
                  >
                    <option value="">Select a property</option>
                    {properties.map(p => (
                      <option key={p._id} value={p._id}>{p.title}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Check In *</label>
                    <input 
                      type="date" 
                      className="form-input" 
                      value={checkIn} 
                      onChange={e => setCheckIn(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Check Out *</label>
                    <input 
                      type="date" 
                      className="form-input" 
                      value={checkOut} 
                      onChange={e => setCheckOut(e.target.value)}
                      min={checkIn || new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </div>
                {availability && (
                  <div style={{ 
                    padding: 12, 
                    borderRadius: 8, 
                    background: availability.isAvailable ? '#ECFDF5' : '#FEF2F2',
                    color: availability.isAvailable ? '#059669' : '#DC2626',
                    fontSize: 14,
                    fontWeight: 500
                  }}>
                    {availability.isAvailable ? '✓ Available for selected dates' : '✗ Not available for selected dates'}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Guests</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Adults</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={guests.adults} 
                      onChange={e => setGuests({ ...guests, adults: parseInt(e.target.value) || 1 })}
                      min={1}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Children</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={guests.children} 
                      onChange={e => setGuests({ ...guests, children: parseInt(e.target.value) || 0 })}
                      min={0}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Infants</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      value={guests.infants} 
                      onChange={e => setGuests({ ...guests, infants: parseInt(e.target.value) || 0 })}
                      min={0}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Additional Details</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 20 }}>
                <div className="form-group">
                  <label className="form-label">Special Requests</label>
                  <textarea 
                    className="form-textarea" 
                    rows={3} 
                    placeholder="Any special requests..."
                    value={specialRequests}
                    onChange={e => setSpecialRequests(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Guest Message</label>
                  <textarea 
                    className="form-textarea" 
                    rows={3} 
                    placeholder="Message from guest..."
                    value={guestMessage}
                    onChange={e => setGuestMessage(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Pricing Summary */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Booking Summary</span></div>
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ color: '#6B7280' }}>Nights</span>
                    <span style={{ fontWeight: 600 }}>{nights}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ color: '#6B7280' }}>Guests</span>
                    <span style={{ fontWeight: 600 }}>{guests.adults + guests.children + guests.infants}</span>
                  </div>
                </div>
              </div>
            </div>

            {pricing && nights > 0 && (
              <div className="card">
                <div className="card-header"><span className="card-title">Pricing</span></div>
                <div style={{ padding: 20 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#6B7280' }}>
                      <span>Base Price</span>
                      <span>${pricing.basePrice} × {nights} nights</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#6B7280' }}>
                      <span>Subtotal</span>
                      <span>${(pricing.basePrice * nights).toLocaleString()}</span>
                    </div>
                    {pricing.cleaningFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#6B7280' }}>
                        <span>Cleaning Fee</span>
                        <span>${pricing.cleaningFee}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#6B7280' }}>
                      <span>Service Fee (5%)</span>
                      <span>${Math.round(pricing.basePrice * nights * 0.05)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#6B7280' }}>
                      <span>Taxes (10%)</span>
                      <span>${Math.round((pricing.basePrice * nights + (pricing.cleaningFee || 0)) * 0.1)}</span>
                    </div>
                    {pricing.securityDeposit > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#6B7280' }}>
                        <span>Security Deposit</span>
                        <span>${pricing.securityDeposit}</span>
                      </div>
                    )}
                    <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16 }}>
                      <span>Total</span>
                      <span>${(pricing.basePrice * nights + Math.round(pricing.basePrice * nights * 0.05) + Math.round((pricing.basePrice * nights + (pricing.cleaningFee || 0)) * 0.1) + (pricing.cleaningFee || 0)).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-primary" 
              style={{ width: '100%' }}
              disabled={submitting || !availability?.isAvailable}
            >
              {submitting ? <div className="spinner" style={{ borderTopColor: '#fff' }} /> : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (!booking) return <div className="empty-state"><h3>Booking not found</h3></div>;

  const STATUS_COLORS = {
    pending: '#D97706', confirmed: '#059669', checked_in: '#2563EB',
    checked_out: '#6B7280', cancelled: '#DC2626'
  };
  const STATUS_BG = {
    pending: '#FFFBEB', confirmed: '#ECFDF5', checked_in: '#EFF6FF',
    checked_out: '#F3F4F6', cancelled: '#FEF2F2'
  };

  const canAdvance = STATUS_FLOW.includes(booking.status) &&
    STATUS_FLOW.indexOf(booking.status) < STATUS_FLOW.length - 1;

  const nextStatus = canAdvance ? STATUS_FLOW[STATUS_FLOW.indexOf(booking.status) + 1] : null;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/bookings')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">Booking #{booking.bookingNumber}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <span className="badge" style={{ color: STATUS_COLORS[booking.status], background: STATUS_BG[booking.status] }}>
                {booking.status?.replace('_', ' ')}
              </span>
              <span className={`badge ${booking.payment?.status === 'paid' ? 'badge-green' : 'badge-yellow'}`}>
                Payment: {booking.payment?.status}
              </span>
            </div>
          </div>
        </div>
        {canManage && booking.status !== 'cancelled' && booking.status !== 'checked_out' && (
          <div style={{ display: 'flex', gap: 8 }}>
            {nextStatus && (
              <button className="btn btn-success" onClick={() => updateStatus(nextStatus)} disabled={updating}>
                {updating ? <div className="spinner" style={{ borderTopColor: '#fff' }} /> : <CheckCircle size={16} />}
                Mark as {nextStatus.replace('_', ' ')}
              </button>
            )}
            <button className="btn btn-danger" onClick={cancelBooking} disabled={updating}>
              <XCircle size={16} /> Cancel
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Booking Summary */}
          <div className="card">
            <div className="card-header"><span className="card-title">Booking Summary</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>CHECK IN</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{format(new Date(booking.checkIn), 'MMM d, yyyy')}</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{booking.property?.rules?.checkInTime || '3:00 PM'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>CHECK OUT</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{format(new Date(booking.checkOut), 'MMM d, yyyy')}</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{booking.property?.rules?.checkOutTime || '11:00 AM'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 16, paddingTop: 16, borderTop: '1px solid #F3F4F6' }}>
              <div><div style={{ fontSize: 12, color: '#9CA3AF' }}>NIGHTS</div><div style={{ fontSize: 16, fontWeight: 600 }}>{booking.nights}</div></div>
              <div><div style={{ fontSize: 12, color: '#9CA3AF' }}>GUESTS</div><div style={{ fontSize: 16, fontWeight: 600 }}>{(booking.guests?.adults || 1) + (booking.guests?.children || 0)}</div></div>
              <div><div style={{ fontSize: 12, color: '#9CA3AF' }}>SOURCE</div><div style={{ fontSize: 16, fontWeight: 600, textTransform: 'capitalize' }}>{booking.source}</div></div>
            </div>
          </div>

          {/* Property */}
          <div className="card">
            <div className="card-header"><span className="card-title">Property</span></div>
            <div style={{ display: 'flex', gap: 12, cursor: 'pointer' }} onClick={() => navigate(`/properties/${booking.property?._id}`)}>
              <Home size={20} color="#3B82F6" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600 }}>{booking.property?.title}</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>
                  {booking.property?.address?.city}, {booking.property?.address?.country}
                </div>
              </div>
            </div>
          </div>

          {/* Check-in Details (editable) */}
          {canManage && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Check-in Details</span>
                <button className="btn btn-sm btn-primary" onClick={() => updateStatus(booking.status)} disabled={updating}>
                  Save
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Access Code</label>
                  <input className="form-input" placeholder="e.g. 1234# or smart lock code" value={checkInCode} onChange={e => setCheckInCode(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Check-in Instructions</label>
                  <textarea className="form-textarea" rows={4} placeholder="Parking info, how to access the property..." value={checkInInstructions} onChange={e => setCheckInInstructions(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Internal Notes</label>
                  <textarea className="form-textarea" rows={3} placeholder="Staff notes (not visible to guests)" value={internalNotes} onChange={e => setInternalNotes(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Guest message */}
          {booking.guestMessage && (
            <div className="card">
              <div className="card-header"><span className="card-title">Guest Message</span></div>
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{booking.guestMessage}</p>
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Guest Info */}
          <div className="card">
            <div className="card-header"><span className="card-title">Guest</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div className="avatar avatar-lg">{booking.guest?.firstName?.[0]}{booking.guest?.lastName?.[0]}</div>
              <div>
                <div style={{ fontWeight: 600 }}>{booking.guest?.firstName} {booking.guest?.lastName}</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{booking.guest?.email}</div>
                {booking.guest?.phone && <div style={{ fontSize: 13, color: '#6B7280' }}>{booking.guest?.phone}</div>}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="card">
            <div className="card-header"><span className="card-title">Pricing</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                ['Base Price', `$${booking.pricing?.basePrice} × ${booking.nights} nights`, false],
                ['Subtotal', `$${booking.pricing?.subtotal?.toLocaleString()}`, false],
                ['Cleaning Fee', `$${booking.pricing?.cleaningFee || 0}`, false],
                ['Service Fee', `$${booking.pricing?.serviceFee || 0}`, false],
                ['Taxes', `$${booking.pricing?.taxes || 0}`, false],
                booking.pricing?.discountAmount > 0 && ['Discount', `-$${booking.pricing?.discountAmount}`, false],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#6B7280' }}>
                  <span>{k}</span><span>{v}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16 }}>
                <span>Total</span>
                <span>${booking.pricing?.total?.toLocaleString()}</span>
              </div>
              {booking.pricing?.securityDeposit > 0 && (
                <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'right' }}>
                  + ${booking.pricing?.securityDeposit} security deposit
                </div>
              )}
            </div>
          </div>

          {/* Payment Status */}
          <div className="card">
            <div className="card-header"><span className="card-title">Payment</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: '#6B7280' }}>Status</span>
                <span className={`badge ${booking.payment?.status === 'paid' ? 'badge-green' : booking.payment?.status === 'refunded' ? 'badge-purple' : 'badge-yellow'}`}>{booking.payment?.status}</span>
              </div>
              {booking.payment?.paidAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: '#6B7280' }}>Paid At</span>
                  <span>{format(new Date(booking.payment?.paidAt), 'MMM d, yyyy')}</span>
                </div>
              )}
              {canManage && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>
                  {booking.payment?.status !== 'paid' && (
                    <button 
                      className="btn btn-sm btn-success" 
                      onClick={() => updatePaymentStatus('paid')}
                      disabled={updating}
                      style={{ flex: 1 }}
                    >
                      Mark Paid
                    </button>
                  )}
                  {booking.payment?.status === 'paid' && (
                    <button 
                      className="btn btn-sm btn-danger" 
                      onClick={() => updatePaymentStatus('refunded')}
                      disabled={updating}
                      style={{ flex: 1 }}
                    >
                      Refund
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Cancellation info */}
          {booking.cancellation?.cancelledAt && (
            <div className="card" style={{ border: '1px solid #FECACA' }}>
              <div className="card-header"><span className="card-title" style={{ color: '#DC2626' }}>Cancellation</span></div>
              <div style={{ fontSize: 14, color: '#374151' }}>
                <div>Cancelled on {format(new Date(booking.cancellation.cancelledAt), 'MMM d, yyyy')}</div>
                {booking.cancellation.reason && <div style={{ marginTop: 4, color: '#6B7280' }}>{booking.cancellation.reason}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingDetailPage;
